export const translations = {
  en: {
    nav: {
      title: 'Automata AI',
      product: 'Product',
      solutions: 'Solutions',
      enterprise: 'Enterprise',
      pricing: 'Pricing',
      resources: 'Resources',
      talkToSales: 'Talk to Sales',
      logIn: 'Log In',
      signUp: 'Sign Up'
    },
    home: {
      hero: {
        title: 'AI-Powered Business Automation',
        subtitle: 'Transform your customer service and business processes with custom AI solutions. Start with a proof of concept and scale based on your needs.',
        startPOC: 'Start POC',
        scheduleDemo: 'Schedule Demo'
      },
      demo: {
        title: 'Try Our AI Assistant',
        subtitle: 'Interact with our AI assistant and experience the power of our solution',
        placeholder: 'Type your message...',
        send: 'Send',
        initial: 'Hello! How can I assist you today?'
      },
      features: {
        title: 'Key Features',
        subtitle: 'Discover the key features that make our solution unique',
        items: [
          {
            title: 'Fast Implementation',
            description: 'Get started with a 30-60 day proof of concept to validate the solution.'
          },
          {
            title: 'Customized AI',
            description: 'AI models adapted to your specific processes and requirements.'
          },
          {
            title: 'Flexible Integration',
            description: 'Connect with your existing systems and workflows.'
          },
          {
            title: 'Enterprise Security',
            description: 'Maximum data protection and regulatory compliance.'
          }
        ]
      }
    },
    solutions: {
      hero: {
        title: 'Flexible Solutions for Every Stage',
        subtitle: 'Start with a proof of concept and scale according to your needs',
        startPOC: 'Start POC',
        contactSales: 'Contact Sales'
      },
      starter: {
        title: 'Starter (POC)',
        subtitle: 'Proof of Concept',
        features: [
          'Implementation in a specific area of your business',
          '30-60 day trial with dedicated support',
          'Controlled volume of interactions',
          'Results and ROI evaluation'
        ]
      },
      enterprise: {
        title: 'Enterprise',
        subtitle: 'Customized Solution',
        features: [
          'Complete and personalized implementation',
          'Integration with existing systems',
          'Customized AI training',
          'Premium support and guaranteed SLA'
        ]
      },
      features: {
        title: 'Core Features',
        subtitle: 'Features designed to empower your business',
        items: {
          customerService: {
            title: "Customer Service",
            description: "Automate customer service with precise and personalized responses",
            benefits: [
              "Instant Responses",
              "Customer Personalization",
              "Continuous Learning",
              "Intelligent Escalation"
            ]
          },
          sentiment: {
            title: "Sentiment Analysis",
            description: "Understand your customers' emotions and needs",
            benefits: [
              "Emotion Detection",
              "Dissatisfaction Alerts",
              "Detailed Reports",
              "Continuous Improvement"
            ]
          },
          reservations: {
            title: "Reservation Management",
            description: "Automate the reservation and scheduling process",
            benefits: [
              "24/7 Reservations",
              "Automatic Confirmations",
              "Reminders",
              "Cancellation Management"
            ]
          },
          multilingual: {
            title: "Multilingual Support",
            description: "Serve your customers in multiple languages",
            benefits: [
              "Real-time Translation",
              "Language Detection",
              "Culturally Appropriate Responses",
              "Global Expansion"
            ]
          }
        }
      },
      tabs: {
        byIndustry: 'By Industry',
        byFeature: 'By Feature',
        integrations: 'Integrations'
      },
      industries: {
        title: 'Industry Solutions',
        subtitle: 'Discover how our AI can transform your sector',
        items: {
          ecommerce: {
            title: 'E-commerce',
            description: 'Optimize shopping experience and customer service',
            benefits: [
              '24/7 Customer Support',
              'Personalized Recommendations',
              'Order Management',
              'After-sales Support'
            ]
          },
          financial: {
            title: 'Financial Services',
            description: 'Automate operations and improve customer service',
            benefits: [
              'Balance and Transaction Queries',
              'Transfer Management',
              'Financial Product Support',
              'Fraud Detection'
            ]
          },
          healthcare: {
            title: 'Healthcare & Wellness',
            description: 'Improve patient experience and appointment management',
            benefits: [
              'Appointment Scheduling',
              'Treatment Follow-up',
              'Basic Medical Queries',
              'Personalized Reminders'
            ]
          },
          education: {
            title: 'Education',
            description: 'Enhance learning experience with intelligent assistance',
            benefits: [
              'Academic Query Responses',
              'Enrollment Management',
              'Progress Tracking',
              'Personalized Educational Resources'
            ]
          },
          tourism: {
            title: 'Tourism',
            description: 'Optimize your customers travel experience',
            benefits: [
              'Automated Reservations',
              'Destination Information',
              'Itinerary Assistance',
              'Multilingual Support'
            ]
          }
        }
      },
      integrations: {
        title: 'Integration Platforms',
        description: 'Connect with your favorite messaging platforms',
        items: [
          {
            title: 'Chat Platforms',
            description: 'Integrate the chatbot with multiple communication channels',
            benefits: [
              'WhatsApp Business',
              'Facebook Messenger',
              'Telegram',
              'Custom web chat'
            ]
          }
        ]
      },
      selectedSolution: {
        benefits: "Key Benefits",
        explore: "Explore Solution"
      },
      cta: {
        title: "Need a Custom Solution?",
        description: "Our team is ready to help you find the perfect solution for your business",
        button: "Contact Sales"
      }
    },
    enterprise: {
      hero: {
        title: 'Enterprise Solutions',
        subtitle: 'Empower your business with advanced AI and dedicated enterprise-level support',
        contactSales: 'Contact Sales',
        watchDemo: 'Watch Demo'
      },
      features: {
        title: 'Enterprise Features',
        items: {
          security: {
            title: 'Enterprise Security',
            description: 'Maximum data protection with military-grade encryption and regulatory compliance'
          },
          teams: {
            title: 'Team Management',
            description: 'Advanced role and permission management for large teams'
          },
          customization: {
            title: 'Total Customization',
            description: 'Complete adaptation to your existing processes and workflows'
          },
          analytics: {
            title: 'Advanced Analytics',
            description: 'Detailed metrics and insights for data-driven decisions'
          },
          support: {
            title: 'Premium Support',
            description: '24/7 dedicated support with guaranteed response times'
          },
          training: {
            title: 'Custom Training',
            description: 'Personalized training and onboarding for your teams'
          }
        }
      },
      benefits: {
        title: 'Enterprise Benefits',
        items: [
          'Personalized implementation and configuration',
          'Dedicated account manager',
          'Dedicated infrastructure',
          'AI model customization',
          'Guaranteed response time SLA',
          'Training and onboarding for teams',
          'Advanced backup and recovery',
          'Priority technical support'
        ]
      },
      security: {
        title: 'Enterprise-Grade Security',
        description: 'We implement the strictest security measures and regulatory compliance to protect your data and operations',
        cta: 'View Certifications'
      },
      cta: {
        title: 'Ready to empower your business?',
        description: 'Our enterprise team is ready to help you implement the perfect solution for your organization',
        schedule: 'Schedule Consultation',
        brochure: 'Download Brochure'
      }
    },
    product: {
      hero: {
        title: 'AI-Powered Business Automation',
        subtitle: 'Transform your customer service and business processes with custom AI solutions. Start with a proof of concept and scale based on your needs.',
        cta: 'Get Started'
      },
      capabilities: {
        title: 'Core Capabilities',
        subtitle: 'Powerful features to transform your business',
        items: {
          ai: {
            title: 'Advanced Conversational AI',
            description: 'Natural language processing for human-like interactions'
          },
          integration: {
            title: 'Multichannel Integration',
            description: 'Connect with your favorite messaging platforms'
          },
          customization: {
            title: 'Total Customization',
            description: 'Tailored solutions for your specific needs'
          },
          analytics: {
            title: 'Real-Time Analytics',
            description: 'Monitor and optimize performance in real-time'
          }
        }
      },
      benefits: {
        title: 'Key Benefits',
        subtitle: 'Why choose our solution',
        items: {
          support: {
            title: '24/7 Support',
            description: 'Always available to assist your customers'
          },
          scalability: {
            title: 'Instant Scalability',
            description: 'Grow your capacity instantly as needed'
          },
          automation: {
            title: 'Intelligent Automation',
            description: 'Automate repetitive tasks efficiently'
          },
          security: {
            title: 'Enterprise Security',
            description: 'Bank-grade security for your data'
          }
        }
      },
      technology: {
        title: 'Our Technology',
        subtitle: 'Built with cutting-edge technology',
        items: {
          nlp: {
            title: 'Natural Language Processing',
            description: 'Advanced language understanding capabilities'
          },
          data: {
            title: 'Intelligent Data Management',
            description: 'Secure and efficient data processing'
          },
          api: {
            title: 'Robust APIs',
            description: 'Easy integration with your systems'
          }
        }
      },
      cta: {
        title: "Transform Your Business with AI",
        description: "Start with a proof of concept and see the results for yourself",
        startPOC: "Start Proof of Concept",
        talkExpert: "Talk to an Expert"
      }
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to common questions about our AI solution',
      questions: [
        {
          q: 'How does the AI chatbot learn my business?',
          a: 'Our AI chatbot uses advanced machine learning to understand your business through your documentation, website content, and past customer interactions. The more it interacts, the smarter it becomes.'
        },
        {
          q: 'How long does implementation take?',
          a: 'Basic implementation can be completed in as little as 24 hours. For more complex integrations and custom training, it typically takes 3-5 business days.'
        },
        {
          q: 'Is my data secure?',
          a: 'Yes, we use enterprise-grade encryption and follow strict data protection protocols. All data is stored in secure, compliant facilities, and we\'re GDPR compliant.'
        }
      ]
    },
    contact: {
      title: 'Get in Touch',
      subtitle: 'Get in touch with our team and start your AI journey today',
      name: 'Your Name',
      email: 'Your Email',
      message: 'Your Message',
      send: 'Send Message'
    },
    blog: {
      title: 'Latest Insights',
      readMore: 'Read More'
    },
    footer: {
      description: 'Transforming customer service with intelligent AI solutions.',
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      rights: '© 2024 AI ChatBot Solutions. All rights reserved.'
    },
    poc: {
      hero: {
        title: "Start Your Proof of Concept",
        subtitle: "Experience the power of our AI solution with a tailored proof of concept for your business",
        scheduleConsultation: "Schedule Consultation",
        viewProcess: "View Process"
      },
      process: {
        title: "Implementation Process",
        steps: {
          objectives: {
            title: "Define Objectives",
            description: "We work together to identify your specific needs and set clear goals"
          },
          analysis: {
            title: "Initial Analysis",
            description: "Analysis of your current processes and integration requirements"
          },
          setup: {
            title: "Configuration",
            description: "Setup and customization of the AI solution for your use case"
          },
          pilot: {
            title: "Pilot Testing",
            description: "Controlled testing phase with real scenarios and feedback collection"
          }
        }
      },
      benefits: {
        title: "POC Benefits",
        items: {
          risk: {
            title: "Minimal Risk",
            description: "Try our solution with no long-term commitment"
          },
          results: {
            title: "Quick Results",
            description: "See tangible results within 30-60 days"
          },
          customization: {
            title: "Total Customization",
            description: "Solution adapted to your specific needs"
          },
          support: {
            title: "Dedicated Support",
            description: "Expert guidance throughout the process"
          }
        }
      },
      cta: {
        title: "Ready to Get Started?",
        description: "Schedule a consultation with our team to begin your proof of concept",
        schedule: "Schedule Now",
        resources: "View Resources"
      }
    },
    contactSales: {
      title: 'Schedule a Call With Our Team',
      subtitle: 'Schedule a 30-minute consultation to explore how our AI-powered business automation can optimize your processes and drive efficiency',
      sections: {
        needs: {
          title: 'Discuss Your Needs',
          description: 'Explore how AI automation can transform your business processes'
        },
        solutions: {
          title: 'Custom Solutions',
          description: 'Get an AI strategy designed for your specific workflow'
        },
        pricing: {
          title: 'Pricing Details',
          description: 'Find the perfect plan to scale your automation journey'
        }
      }
    }
  },
  es: {
    nav: {
      title: 'Automata AI',
      product: 'Producto',
      solutions: 'Soluciones',
      enterprise: 'Empresa',
      pricing: 'Precios',
      resources: 'Recursos',
      talkToSales: 'Hablar con Ventas',
      logIn: 'Iniciar Sesión',
      signUp: 'Registrarse'
    },
    home: {
      hero: {
        title: 'Automatización Inteligente para tu Empresa',
        subtitle: 'Transforma tu servicio al cliente y procesos empresariales con IA personalizada. Comienza con una prueba de concepto y escala según tus necesidades.',
        startPOC: 'Comenzar POC',
        scheduleDemo: 'Agendar Demo'
      },
      demo: {
        title: 'Prueba Nuestro Asistente IA',
        subtitle: 'Interactúa con nuestro asistente IA y experimenta el poder de nuestra solución',
        placeholder: 'Escribe tu mensaje...',
        send: 'Enviar',
        initial: '¡Hola! ¿En qué puedo ayudarte hoy?'
      },
      features: {
        title: 'Características Principales',
        subtitle: 'Descubre las características clave que hacen única nuestra solución',
        items: [
          {
            title: 'Implementación Rápida',
            description: 'Comienza con una prueba de concepto de 30-60 días para validar la solución.'
          },
          {
            title: 'IA Personalizada',
            description: 'Modelos de IA adaptados a tus procesos y requisitos específicos.'
          },
          {
            title: 'Integración Flexible',
            description: 'Conexión con tus sistemas y flujos de trabajo existentes.'
          },
          {
            title: 'Seguridad Empresarial',
            description: 'Máxima protección de datos y cumplimiento normativo.'
          }
        ]
      }
    },
    solutions: {
      hero: {
        title: 'Soluciones Flexibles para Cada Etapa',
        subtitle: 'Comienza con una prueba de concepto y escala según tus necesidades',
        startPOC: 'Comenzar POC',
        contactSales: 'Contactar Ventas'
      },
      starter: {
        title: 'Inicial (POC)',
        subtitle: 'Prueba de Concepto',
        features: [
          'Implementación en un área específica de tu negocio',
          'Prueba de 30-60 días con soporte dedicado',
          'Volumen controlado de interacciones',
          'Evaluación de resultados y ROI'
        ]
      },
      enterprise: {
        title: 'Empresarial',
        subtitle: 'Solución Personalizada',
        features: [
          'Implementación completa y personalizada',
          'Integración con sistemas existentes',
          'Entrenamiento de IA personalizado',
          'Soporte premium y SLA garantizado'
        ]
      },
      tabs: {
        byIndustry: 'Por Industria',
        byFeature: 'Por Característica',
        integrations: 'Integraciones'
      },
      features: {
        title: 'Características Principales',
        subtitle: 'Características diseñadas para potenciar tu negocio',
        items: {
          customerService: {
            title: "Servicio al Cliente",
            description: "Automatiza el servicio al cliente con respuestas precisas y personalizadas",
            benefits: [
              "Respuestas Instantáneas",
              "Personalización del Cliente",
              "Aprendizaje Continuo",
              "Escalamiento Inteligente"
            ]
          },
          sentiment: {
            title: "Análisis de Sentimientos",
            description: "Comprende las emociones y necesidades de tus clientes",
            benefits: [
              "Detección de Emociones",
              "Alertas de Insatisfacción",
              "Informes Detallados",
              "Mejora Continua"
            ]
          },
          reservations: {
            title: "Gestión de Reservas",
            description: "Automatiza el proceso de reservas y programación",
            benefits: [
              "Reservas 24/7",
              "Confirmaciones Automáticas",
              "Recordatorios",
              "Gestión de Cancelaciones"
            ]
          },
          multilingual: {
            title: "Soporte Multilingüe",
            description: "Atiende a tus clientes en múltiples idiomas",
            benefits: [
              "Traducción en Tiempo Real",
              "Detección de Idioma",
              "Respuestas Culturalmente Apropiadas",
              "Expansión Global"
            ]
          }
        }
      },
      industries: {
        title: 'Soluciones por Industria',
        subtitle: 'Descubre cómo nuestra IA puede transformar tu sector',
        items: {
          ecommerce: {
            title: 'E-commerce',
            description: 'Optimiza la experiencia de compra y el servicio al cliente',
            benefits: [
              'Soporte al Cliente 24/7',
              'Recomendaciones Personalizadas',
              'Gestión de Pedidos',
              'Soporte Post-venta'
            ]
          },
          financial: {
            title: 'Servicios Financieros',
            description: 'Automatiza operaciones y mejora la atención al cliente',
            benefits: [
              'Consultas de saldo y movimientos',
              'Gestión de transferencias',
              'Soporte en productos financieros',
              'Detección de fraude'
            ]
          },
          healthcare: {
            title: 'Salud y Bienestar',
            description: 'Mejora la experiencia del paciente y la gestión de citas',
            benefits: [
              'Agendamiento de citas',
              'Seguimiento de tratamientos',
              'Consultas médicas básicas',
              'Recordatorios personalizados'
            ]
          },
          education: {
            title: 'Educación',
            description: 'Mejora la experiencia de aprendizaje con asistencia inteligente',
            benefits: [
              'Respuestas a dudas académicas',
              'Gestión de inscripciones',
              'Seguimiento de progreso',
              'Recursos educativos personalizados'
            ]
          },
          tourism: {
            title: 'Turismo',
            description: 'Optimiza la experiencia de viaje de tus clientes',
            benefits: [
              'Reservas automatizadas',
              'Información de destinos',
              'Asistencia en itinerarios',
              'Atención multilingüe'
            ]
          }
        }
      },
      integrations: {
        title: 'Plataformas de Integración',
        description: 'Conecta con tus plataformas de mensajería favoritas',
        items: [
          {
            title: 'Plataformas de Chat',
            description: 'Integra el chatbot con múltiples canales de comunicación',
            benefits: [
              'WhatsApp Business',
              'Facebook Messenger',
              'Telegram',
              'Chat web personalizado'
            ]
          }
        ]
      },
      selectedSolution: {
        benefits: "Beneficios Clave",
        explore: "Explorar Solución"
      },
      cta: {
        title: "¿Necesitas una solución personalizada?",
        description: "Nuestro equipo está listo para ayudarte a encontrar la solución perfecta para tu negocio",
        button: "Contactar con Ventas"
      }
    },
    enterprise: {
      hero: {
        title: 'Soluciones Enterprise',
        subtitle: 'Potencia tu empresa con IA avanzada y soporte dedicado de nivel empresarial',
        contactSales: 'Contactar Ventas',
        watchDemo: 'Ver Demo'
      },
      features: {
        title: 'Características Enterprise',
        items: {
          security: {
            title: 'Seguridad Empresarial',
            description: 'Máxima protección de datos con cifrado de nivel militar y cumplimiento normativo'
          },
          teams: {
            title: 'Gestión de Equipos',
            description: 'Administración avanzada de roles y permisos para grandes equipos'
          },
          customization: {
            title: 'Personalización Total',
            description: 'Adaptación completa a tus procesos y flujos de trabajo existentes'
          },
          analytics: {
            title: 'Análisis Avanzado',
            description: 'Métricas detalladas e insights para decisiones basadas en datos'
          },
          support: {
            title: 'Soporte Premium',
            description: 'Soporte dedicado 24/7 con tiempos de respuesta garantizados'
          },
          training: {
            title: 'Capacitación Personalizada',
            description: 'Entrenamiento y onboarding personalizado para tus equipos'
          }
        }
      },
      benefits: {
        title: 'Beneficios Enterprise',
        items: [
          'Implementación y configuración personalizada',
          'Gestor de cuenta dedicado',
          'Infraestructura dedicada',
          'Personalización de modelos IA',
          'SLA con tiempo de respuesta garantizado',
          'Capacitación y onboarding para equipos',
          'Backup y recuperación avanzada',
          'Soporte técnico prioritario'
        ]
      },
      security: {
        title: 'Seguridad de Nivel Enterprise',
        description: 'Implementamos las más estrictas medidas de seguridad y cumplimiento normativo para proteger tus datos y operaciones',
        cta: 'Ver Certificaciones'
      },
      cta: {
        title: '¿Listo para potenciar tu empresa?',
        description: 'Nuestro equipo enterprise está listo para ayudarte a implementar la solución perfecta para tu organización',
        schedule: 'Agendar Consulta',
        brochure: 'Descargar Brochure'
      }
    },
    product: {
      hero: {
        title: 'Automatización Inteligente para tu Empresa',
        subtitle: 'Transforma tu servicio al cliente y procesos empresariales con IA personalizada. Comienza con una prueba de concepto y escala según tus necesidades.',
        cta: 'Comenzar'
      },
      capabilities: {
        title: 'Capacidades Principales',
        subtitle: 'Funcionalidades potentes para transformar tu negocio',
        items: {
          ai: {
            title: 'IA Conversacional Avanzada',
            description: 'Procesamiento de lenguaje natural para interacciones humanas'
          },
          integration: {
            title: 'Integración Multicanal',
            description: 'Conéctate con tus plataformas de mensajería favoritas'
          },
          customization: {
            title: 'Personalización Completa',
            description: 'Soluciones adaptadas a tus necesidades específicas'
          },
          analytics: {
            title: 'Análisis en Tiempo Real',
            description: 'Monitorea y optimiza el rendimiento en tiempo real'
          }
        }
      },
      benefits: {
        title: 'Beneficios Clave',
        subtitle: 'Por qué elegir nuestra solución',
        items: {
          support: {
            title: 'Soporte 24/7',
            description: 'Siempre disponible para asistir a tus clientes'
          },
          scalability: {
            title: 'Escalabilidad Instantánea',
            description: 'Aumenta tu capacidad al instante según lo necesites'
          },
          automation: {
            title: 'Automatización Inteligente',
            description: 'Automatiza tareas repetitivas de manera eficiente'
          },
          security: {
            title: 'Seguridad Empresarial',
            description: 'Seguridad de nivel bancario para tus datos'
          }
        }
      },
      technology: {
        title: 'Nuestra Tecnología',
        subtitle: 'Construido con tecnología de vanguardia',
        items: {
          nlp: {
            title: 'Procesamiento de Lenguaje Natural',
            description: 'Capacidades avanzadas de comprensión del lenguaje'
          },
          data: {
            title: 'Gestión Inteligente de Datos',
            description: 'Procesamiento de datos seguro y eficiente'
          },
          api: {
            title: 'APIs Robustas',
            description: 'Fácil integración con tus sistemas'
          }
        }
      },
      cta: {
        title: "Transforma tu Negocio con IA",
        description: "Comienza con una prueba de concepto y comprueba los resultados por ti mismo",
        startPOC: "Comenzar Prueba de Concepto",
        talkExpert: "Hablar con un Experto"
      }
    },
    faq: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Encuentra respuestas a preguntas comunes sobre nuestra solución de IA',
      questions: [
        {
          q: '¿Cómo aprende el chatbot IA sobre mi negocio?',
          a: 'Nuestro chatbot IA utiliza aprendizaje automático avanzado para entender tu negocio a través de tu documentación, contenido web e interacciones pasadas con clientes. Cuanto más interactúa, más inteligente se vuelve.'
        },
        {
          q: '¿Cuánto tiempo toma la implementación?',
          a: 'La implementación básica puede completarse en tan solo 24 horas. Para integraciones más complejas y entrenamiento personalizado, típicamente toma de 3 a 5 días hábiles.'
        },
        {
          q: '¿Están seguros mis datos?',
          a: 'Sí, utilizamos encriptación de nivel empresarial y seguimos estrictos protocolos de protección de datos. Todos los datos se almacenan en instalaciones seguras y cumplimos con GDPR.'
        }
      ]
    },
    contact: {
      title: 'Contáctanos',
      subtitle: 'Ponte en contacto con nuestro equipo y comienza tu viaje con IA hoy',
      name: 'Tu Nombre',
      email: 'Tu Email',
      message: 'Tu Mensaje',
      send: 'Enviar Mensaje'
    },
    blog: {
      title: 'Últimas Novedades',
      readMore: 'Leer Más'
    },
    footer: {
      description: 'Transformando el servicio al cliente con soluciones inteligentes de IA.',
      product: 'Producto',
      company: 'Empresa',
      legal: 'Legal',
      rights: '© 2024 Soluciones ChatBot IA. Todos los derechos reservados.'
    },
    poc: {
      hero: {
        title: "Comienza tu Prueba de Concepto",
        subtitle: "Experimenta el poder de nuestra solución de IA con una prueba de concepto adaptada a tu negocio",
        scheduleConsultation: "Agendar Consulta",
        viewProcess: "Ver Proceso"
      },
      process: {
        title: "Proceso de Implementación",
        steps: {
          objectives: {
            title: "Definir Objetivos",
            description: "Trabajamos juntos para identificar tus necesidades específicas y establecer metas claras"
          },
          analysis: {
            title: "Análisis Inicial",
            description: "Análisis de tus procesos actuales y requerimientos de integración"
          },
          setup: {
            title: "Configuración",
            description: "Configuración y personalización de la solución de IA para tu caso de uso"
          },
          pilot: {
            title: "Prueba Piloto",
            description: "Fase de pruebas controlada con escenarios reales y recolección de feedback"
          }
        }
      },
      benefits: {
        title: "Beneficios del POC",
        items: {
          risk: {
            title: "Riesgo Mínimo",
            description: "Prueba nuestra solución sin compromiso a largo plazo"
          },
          results: {
            title: "Resultados Rápidos",
            description: "Obtén resultados tangibles en 30-60 días"
          },
          customization: {
            title: "Personalización Total",
            description: "Solución adaptada a tus necesidades específicas"
          },
          support: {
            title: "Soporte Dedicado",
            description: "Guía experta durante todo el proceso"
          }
        }
      },
      cta: {
        title: "¿Listo para Comenzar?",
        description: "Agenda una consulta con nuestro equipo para comenzar tu prueba de concepto",
        schedule: "Agendar Ahora",
        resources: "Ver Recursos"
      }
    },
    contactSales: {
      title: 'Agenda una Llamada con Nuestro Equipo',
      subtitle: 'Agenda una consulta de 30 minutos para explorar cómo nuestra automatización empresarial con IA puede optimizar tus procesos e impulsar la eficiencia',
      sections: {
        needs: {
          title: 'Discute tus Necesidades',
          description: 'Explora cómo la automatización con IA puede transformar tus procesos empresariales'
        },
        solutions: {
          title: 'Soluciones Personalizadas',
          description: 'Obtén una estrategia de IA diseñada para tu flujo de trabajo específico'
        },
        pricing: {
          title: 'Detalles de Precios',
          description: 'Encuentra el plan perfecto para escalar tu viaje de automatización'
        }
      }
    }
  }
}; 